// src/api/UserAPIUtils.tsx

import axios, { Axios, AxiosError, AxiosProgressEvent } from "axios";
import { AppDispatch } from "../app/store";
import { setRoles } from "../features/auth/AdministrationSlice";
import {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
} from "../features/auth/authSlice";
import {
  setPoliciesAndRole,
  setUserDetails,
} from "../features/auth/userDetailSlice";
import { Course } from "../models/CourseModel";
import { LoginResponse } from "../models/LoginModel";
import { Manager } from "../models/ManagerModel";
import { UserRoleAndPolicies } from "../models/PoliciesModel";
import { RecentActivityModel } from "../models/RecentActivityModel";
import { Role } from "../models/RoleModel";
import { TaskModel } from "../models/TaskModel";
import { User } from "../models/UserModel";
import { generateRandomString } from "../utils/GeneratePassword";
import { del, get, post, put, putFileToS3 } from "./apiUtils";
import { GenericResponse } from "../models/GenericModels";
import {
  CompliancePolicy,
  UpsertCompliancePolicy,
} from "../models/CompliancePolicyModel";
import { replace } from "react-router-dom";
import {
  EquipmentAssignmentWithEquipmentModel,
  StaffEquipmentAssignmentModel,
  StaffEquipmentModel,
} from "../models/StaffEquipmentModel";
import { url } from "inspector";
export const authenticateUser =
  (email: string, password: string) => async (dispatch: AppDispatch) => {
    try {
      dispatch(loginStart());
      const response = await post<LoginResponse>("/api/users/authenticate", {
        email,
        password,
      });

      if (response.token) {
        if (response.userDetails) {
          dispatch(
            loginSuccess({
              token: response.token,
              isPasswordResetRequired: response.isPasswordResetRequired,
            })
          );
          dispatch(setUserDetails({ user: response.userDetails as User }));
          // console.log("login success");
        } else {
          dispatch(loginFailure("User details not found"));
        }
      } else {
        dispatch(loginFailure("Invalid credentials"));
      }
    } catch (error) {
      dispatch(loginFailure("An error occurred during login"));
    }
  };

// Simulated logout action
export const performLogout = () => (dispatch: AppDispatch) => {
  dispatch(logout());
};

export const getUserPolicies =
  (userID: string | null) => async (dispatch: AppDispatch) => {
    try {
      // console.log("Getting user policies");
      const response = await get<UserRoleAndPolicies>(
        `/api/policies/user/${userID}`
      );
      // console.log(response);
      const policies = response.policies.map((policy) => policy.name);
      dispatch(setPoliciesAndRole({ policies, role: response.role }));
    } catch (error) {
      console.error("Failed to get user policies:", error);
      throw error;
    }
  };

export const getAllUsers = async () => {
  try {
    const response = await get<User[]>("/api/users/all");
    return response || [];
  } catch (error) {
    console.error("Failed to get all users", error);
    return [];
  }
};

export const getAllExternalUsers = async () => {
  try {
    const response = await get<User[]>("/api/users/external-users/all");
    return response || [];
  } catch (error) {
    console.error("Failed to get all users", error);
    return [];
  }
};

export const updateInternalUser = async (user: User): Promise<User> => {
  try {
    const userData = {
      fullname: user.fullname,
      email: user.email,
      cellphoneNumber: user.cellphoneNumber,
      gender: user.gender,
      roleId: user.roleId,
      managerId: user.managerId,
    };

    const response = await put<User>(`/api/users/user/${user.id}`, userData);
    if (response && typeof response === "object" && "id" in response) {
      return response as User;
    } else {
      throw new Error("Failed to update user");
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Failed to update user", error);
      throw error;
    } else {
      console.error("An unexpected error occurred", error);
      throw new Error("An unexpected error occurred while updating the user");
    }
  }
};

export const updateExternalUser = async (user: User): Promise<User> => {
  try {
    const userData = {
      fullname: user.fullname,
      email: user.email,
    };

    const response = await put<GenericResponse<User>>(
      `/api/users/external-users/external-user/${user.id}`,
      userData
    );
    if (response.success) {
      return response.data as User;
    } else {
      throw new Error("Failed to update user");
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Failed to update user", error);
      throw error;
    } else {
      console.error("An unexpected error occurred", error);
      throw new Error("An unexpected error occurred while updating the user");
    }
  }
};

export const editProfile = async (user: User): Promise<User | AxiosError> => {
  try {
    const userData = {
      fullname: user.fullname,
      email: user.email,
      cellphoneNumber: user.cellphoneNumber,
      gender: user.gender,
    };

    const response = await put<Partial<User>>(
      `/api/users/edit-profile/${user.id}`,
      userData
    );
    if (response && typeof response === "object" && "email" in response) {
      return response as User;
    } else {
      throw new Error("Failed to insert user");
    }
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to insert user", axiosError);
    return axiosError;
  }
};

export const insertUser = async (user: User): Promise<User> => {
  try {
    const userData = {
      fullname: user.fullname,
      email: user.email,
      cellphoneNumber: user.cellphoneNumber,
      gender: user.gender,
      password: generateRandomString(8),
      roleId: user.roleId,
      managerId: user.managerId,
    };

    const response = await post<User>("/api/users/user", userData);
    if (response && typeof response === "object" && "id" in response) {
      return response as User;
    } else {
      throw new Error("Failed to insert user");
    }
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to insert user", axiosError);
    throw axiosError;
  }
};

export const createExternalUser = async (user: User): Promise<User> => {
  try {
    const userData = {
      fullname: user.fullname,
      email: user.email,
      password: generateRandomString(8),
    };

    const response = await post<GenericResponse<User>>(
      "/api/users/external-users/external-user",
      userData
    );
    if (response.success) {
      return response.data as User;
    } else {
      throw new Error("Failed to create external user");
    }
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Failed to create user", error);
      throw error;
    } else {
      console.error("An unexpected error occurred", error);
      throw new Error("An unexpected error occurred during the operation");
    }
  }
};

export const deleteUser = async (userId: string): Promise<void> => {
  try {
    await del(`/api/users/user/${userId}`);
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to delete user", axiosError);
    throw axiosError;
  }
};

export const getAllRoles = async (dispatch: AppDispatch) => {
  try {
    const response = await get<Role[]>("/api/roles/all");
    if (response) {
      dispatch(setRoles({ roles: response }));
    }
  } catch (error) {
    console.error("Failed to get all roles", error);
    return [];
  }
};

export const forgotPassword = async (email: string) => {
  try {
    const response = await post<{ message: string }>(
      "/api/users/forgot-password",
      { Email: email }
    );
    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Failed to reset password", error);
      return error.response?.data;
    } else {
      console.error("An unexpected error occurred", error);
      throw new Error("An unexpected error occurred during the operation");
    }
  }
};

export const resetPassword = async (newPassword: string, token: string) => {
  try {
    const response = await post<{ message: string }>(
      "/api/users/reset-password",
      { newPassword, Token: token }
    );
    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Failed to reset password", error);
      throw error;
    } else {
      console.error("An unexpected error occurred", error);
      throw new Error("An unexpected error occurred during the operation");
    }
  }
};

export const changePassword = async (
  oldPassword: string,
  newPassword: string,
  userId: string
) => {
  try {
    const response = await put<GenericResponse>(
      `/api/users/change-password/${userId}`,
      { oldPassword, newPassword }
    );
    return response;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error("Failed to change password", error);
      throw error;
    } else {
      console.error("An unexpected error occurred", error);
      throw new Error("An unexpected error occurred during the operation");
    }
  }
};

export const getRecentActivity = async (userId: string) => {
  try {
    const response = await get<{ activities: RecentActivityModel[] }>(
      `/api/analytics/recent-activity/${userId}`
    );
    if (!response.activities) return null;
    const activities = response.activities;
    const groupedActivity = activities.reduce((acc, activity) => {
      const date = new Date(activity.createdAt).toDateString();
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(activity);
      return acc;
    }, {} as Record<string, RecentActivityModel[]>);

    return groupedActivity;
  } catch (error) {
    console.error("Failed to get recent activity", error);
    return null;
  }
};

export const createTask = async (task: TaskModel) => {
  try {
    const response = await post<TaskModel>("/api/tasks/task", { task });
    return response;
  } catch (error) {
    console.error("Failed to create task", error);
    return null;
  }
};

export const getUserTasks = async (userId: string) => {
  try {
    const response = await get<any>(`/api/tasks/user/${userId}`);
    return response;
  } catch (error) {
    console.error("Failed to get user tasks", error);
    return null;
  }
};

export const getManagers = async () => {
  try {
    const response = await get<Manager[]>(`/api/users/managers/all`);
    return response;
  } catch (error) {
    console.error("Failed to get Managers", error);
    return null;
  }
};

export const updateUserTasks = async (userId: string, task: TaskModel) => {
  try {
    const response = await put<TaskModel[]>(
      `/api/tasks/status/${userId}`,
      task
    );
    return response;
  } catch (error) {
    console.error("Failed to update user tasks", error);
    return null;
  }
};

export const updateUserTaskStatus = async (userId: string, status: string) => {
  try {
    const response = await put<TaskModel[]>(`/api/tasks/status/${userId}`, {
      status,
    });
    return response;
  } catch (error) {
    console.error("Unable to update task", error);
    return null;
  }
};

export const uploadBatchUsersFile = async (
  batchUsersFile: File,
  batchFileUploadProgress: (progress: number) => void
): Promise<boolean | AxiosError> => {
  try {
    // console.log("Uploading batch users file");
    const s3SignedUrl = await get<string>(
      "/api/s3/generate-s3-url/batch-users/" + batchUsersFile?.name
    );

    if (!s3SignedUrl) {
      throw new Error("Failed to get signed URL");
    }

    await putFileToS3<void>(
      s3SignedUrl,
      batchUsersFile,
      (progressEvent: AxiosProgressEvent) => {
        const progress = Math.round(
          (progressEvent.loaded * 100) / (progressEvent.total ?? 1)
        );
        // console.log(`Upload Progress: ${progress}%`);
        batchFileUploadProgress(progress);
      }
    );
    return true;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to insert user", axiosError);
    throw axiosError;
  }
};

export const wakeUpDatabase = async () => {
  try {
    const response = await get<{ message: string }>("/api/wake-up/db");
    return response.message;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to wake up database", error);
    throw axiosError;
  }
};

// Fetch all compliance policies
export const getCompliancePolicies = async () => {
  try {
    const response = await get<GenericResponse>("/api/compliance-policies/all");
    return response;
  } catch (error) {
    console.error("Failed to fetch compliance policies", error);
    return {
      success: false,
      message: "Failed to retrieve compliance policies",
    };
  }
};

export const getActiveCompliancePolicies = async () => {
  try {
    const response = await get<GenericResponse>(
      "/api/compliance-policies/active"
    );
    return response;
  } catch (error) {
    console.error("Failed to fetch compliance policies", error);
    return {
      success: false,
      message: "Failed to retrieve compliance policies",
    };
  }
};

// Create a new compliance policy
export const createCompliancePolicy = async (
  compliancePolicy: UpsertCompliancePolicy,
  compliancePolicyPDFUploadProgress: (progress: number) => void
) => {
  try {
    let compliancePolicyUploadURL: string = "";
    try {
      if (compliancePolicy.pdf) {
        compliancePolicyUploadURL = await get<string>(
          "/api/s3/generate-s3-url/platform-storage/system/compliance-policy-templates/" +
            compliancePolicy.pdf?.name.replace(/\s+/g, "-")
        );

        // Remove the extra boolean flag; pass the onUploadProgress callback directly.
        await putFileToS3<void>(
          compliancePolicyUploadURL,
          compliancePolicy.pdf,
          (progressEvent: AxiosProgressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total ?? 1)
            );
            // console.log(`Upload Progress: ${progress}%`);
            compliancePolicyPDFUploadProgress(progress);
          }
        );

        compliancePolicyUploadURL =
          "platform-storage/system/compliance-policy-templates/" +
          compliancePolicy.pdf?.name.replace(/\s+/g, "-");
      }
    } catch (error) {
      console.error("Failed to get SCORM package upload URL", error);
      return { success: false, message: "Failed to upload PDF" };
    }

    const response = await post<GenericResponse>(
      "/api/compliance-policies/policy",
      {
        name: compliancePolicy.name,
        description: compliancePolicy.description,
        pdfUrl: compliancePolicyUploadURL,
      }
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to update compliance policy", axiosError);
    return { success: false, message: axiosError.message };
  }
};

// Update an existing compliance policy
export const updateCompliancePolicy = async (
  compliancePolicyID: string,
  compliancePolicy: UpsertCompliancePolicy,
  compliancePolicyPDFUploadProgress: (progress: number) => void
) => {
  try {
    let compliancePolicyUploadURL: string = "";
    try {
      if (compliancePolicy.pdf) {
        compliancePolicyUploadURL = await get<string>(
          "/api/s3/generate-s3-url/platform-storage/system/compliance-policy-templates/" +
            compliancePolicy.pdf?.name.replace(/\s+/g, "_")
        );

        // Remove the extra boolean flag; pass the onUploadProgress callback directly.
        await putFileToS3<void>(
          compliancePolicyUploadURL,
          compliancePolicy.pdf,
          (progressEvent: AxiosProgressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total ?? 1)
            );
            // console.log(`Upload Progress: ${progress}%`);
            compliancePolicyPDFUploadProgress(progress);
          }
        );

        compliancePolicyUploadURL =
          "platform-storage/system/compliance-policy-templates/" +
          compliancePolicy.pdf?.name.replace(/\s+/g, "_");
      }
    } catch (error) {
      console.error("Failed to get SCORM package upload URL", error);
      return { success: false, message: "Failed to upload PDF" };
    }

    const response = await put<GenericResponse>(
      `/api/compliance-policies/policy/${compliancePolicyID}`,
      {
        name: compliancePolicy.name,
        description: compliancePolicy.description,
        pdfUrl: compliancePolicyUploadURL,
      }
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to update compliance policy", axiosError);
    return { success: false, message: axiosError.message };
  }
};

// Delete a compliance policy
export const deleteCompliancePolicy = async (
  policyId: string
): Promise<void> => {
  try {
    await del(`/api/compliance-policies/policy/${policyId}`);
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to delete compliance policy", axiosError);
    throw axiosError;
  }
};

export const assignCompliancePolicyToUser = async (
  userId: string,
  compliancePolicyId: string
) => {
  try {
    const response = await post<GenericResponse>(
      "/api/compliance-policies/assign",
      {
        compliancePolicyId,
        userId,
      }
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to assign compliance policy to user", axiosError);
    return {
      success: false,
      message: "Failed to assign compliance policy to user",
    };
  }
};

export const unassignCompliancePolicyFromUser = async (
  userId: string,
  compliancePolicyId: string
) => {
  try {
    const response = await post<GenericResponse>(
      "/api/compliance-policies/unassign",
      {
        compliancePolicyId,
        userId,
      }
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to unassign compliance policy to user", axiosError);
    return {
      success: false,
      message: "Failed to unassign compliance policy to user",
    };
  }
};

export const completeCompliancePolicy = async (
  userId: string,
  compliancePolicyId: string
) => {
  try {
    const response = await post<GenericResponse>(
      "/api/compliance-policies/completed",
      {
        compliancePolicyId,
        userId,
      }
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to unassign compliance policy to user", axiosError);
    return {
      success: false,
      message: "Failed to unassign compliance policy to user",
    };
  }
};

export const getUserCompliancePolicies = async (userId: string) => {
  try {
    const response = await get<GenericResponse>(
      `/api/compliance-policies/user/${userId}`
    );
    return response;
  } catch (error) {
    console.error("Failed to fetch compliance policies", error);
    return {
      success: false,
      message: "Failed to retrieve compliance policies",
    };
  }
};

export const resendRegistrationEmail = async (
  email: string
): Promise<{ success: boolean; message?: string; error?: string }> => {
  try {
    const response = await post<{ message: string }>(
      "/api/users/resend-registration-email",
      {
        email: email,
        password: generateRandomString(8),
      }
    );
    return { success: true, message: response.message };
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to resend registration email", axiosError);
    return { success: false, error: axiosError.message };
  }
};

export const getAllStaffEquipment = async (): Promise<
  GenericResponse<StaffEquipmentModel[]>
> => {
  try {
    const response = await get<GenericResponse>(
      `/api/staff-equipment/resource/all`
    );
    return response;
  } catch (error) {
    console.error("Failed to fetch staff equipment", error);
    return {
      success: false,
      message: "Failed to retrieve staff equipment",
    };
  }
};

export const createStaffEquipment = async (
  equipment: Omit<StaffEquipmentModel, "id" | "status">,
  equipmentThumbnail: File | null,
  setThumbnailUploadProgress: (progress: number) => void
): Promise<GenericResponse<StaffEquipmentModel>> => {
  try {
    const response = await post<GenericResponse<StaffEquipmentModel>>(
      "/api/staff-equipment/resource",
      equipment
    );

    if (equipmentThumbnail === null) {
      return response as GenericResponse<StaffEquipmentModel>;
    }

    let thumbnailUploadURL: string = "";
    try {
      if (response.success && equipmentThumbnail && response.data) {
        thumbnailUploadURL = await get<string>(
          `/api/s3/generate-s3-url/scorm-content/staff-equipment/${response.data.id}/thumbnails/` +
            equipmentThumbnail?.name
        );
        await putFileToS3<void>(
          thumbnailUploadURL,
          equipmentThumbnail,
          (progressEvent: AxiosProgressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total ?? 1)
            );
            setThumbnailUploadProgress(progress);
          }
        );
        thumbnailUploadURL =
          `/scorm-content/staff-equipment/${response.data.id}/thumbnails/` +
          equipmentThumbnail?.name;

        const _ = await post<GenericResponse>(
          "/api/staff-equipment/resource/photo/" + response.data?.id,
          {
            url: thumbnailUploadURL,
            primary: true,
          }
        );
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error("Failed to create staff equipment", axiosError);
      return {
        success: false,
        message: axiosError.message,
      };
    }
    response.data!.photoUrls = [thumbnailUploadURL];
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to create staff equipment", axiosError);
    return {
      success: false,
      message: axiosError.message,
    };
  }
};

export const updateEquipment = async (
  staffEquipmentData: Omit<StaffEquipmentModel, "status">,
  equipmentThumbnail: File | null,
  setThumbnailUploadProgress: (progress: number) => void
): Promise<GenericResponse<StaffEquipmentModel>> => {
  try {
    let photoUrls: string[] = staffEquipmentData.photoUrls ?? [];

    if (equipmentThumbnail) {
      try {
        const signedUrl = await get<string>(
          `/api/s3/generate-s3-url/scorm-content/staff-equipment/${staffEquipmentData.id}/thumbnails/` +
            equipmentThumbnail.name
        );

        await putFileToS3<void>(
          signedUrl,
          equipmentThumbnail,
          (progressEvent: AxiosProgressEvent) => {
            const progress = Math.round(
              (progressEvent.loaded * 100) / (progressEvent.total ?? 1)
            );
            setThumbnailUploadProgress(progress);
          }
        );

        const uploadedUrl =
          `scorm-content/staff-equipment/${staffEquipmentData.id}/thumbnails/` +
          equipmentThumbnail.name;

        photoUrls = [uploadedUrl];

        await post<GenericResponse>(
          `/api/staff-equipment/resource/photo/${staffEquipmentData.id}`,
          {
            url: uploadedUrl,
            primary: true,
          }
        );
      } catch (error) {
        const axiosError = error as AxiosError;
        console.error("Failed to upload equipment thumbnail", axiosError);
        return { success: false, message: axiosError.message };
      }
    }

    const response = await put<GenericResponse<StaffEquipmentModel>>(
      "/api/staff-equipment/resource/" + staffEquipmentData.id,
      {
        ...staffEquipmentData,
        photoUrls,
      }
    );

    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to update staff equipment", axiosError);
    return { success: false, message: axiosError.message };
  }
};

export const deleteStaffEquipment = async (
  equipmentId: string
): Promise<GenericResponse<void>> => {
  try {
    await del(`/api/staff-equipment/resource/${equipmentId}`);
    return { success: true, message: "Deleted successfully" };
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to delete staff equipment", axiosError);
    return {
      success: false,
      message: axiosError.message,
    };
  }
};

export const assignStaffEquipmentToUser = async (
  userId: string,
  equipmentId: string
): Promise<GenericResponse<void>> => {
  try {
    const response = await post<GenericResponse>(
      "/api/staff-equipment/resource/assign",
      { userId, equipmentId }
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to assign staff equipment to user", axiosError);
    return { success: false, message: axiosError.message };
  }
};

export const unassignStaffEquipmentFromUser = async (
  assignmentId: string,
  condition: string
): Promise<GenericResponse<void>> => {
  try {
    const response = await post<GenericResponse>(
      "/api/staff-equipment/resource/unassign",
      { assignmentId, condition }
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to unassign staff equipment from user", axiosError);
    return { success: false, message: axiosError.message };
  }
};
export const getAssignedUserByEquipmentID = async (
  equipmentId: string
): Promise<GenericResponse<StaffEquipmentAssignmentModel | null>> => {
  try {
    const response = await get<
      GenericResponse<StaffEquipmentAssignmentModel | null>
    >(`/api/staff-equipment/assigned-user/${equipmentId}`);
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to fetch assigned user", axiosError);
    return { success: false, message: axiosError.message };
  }
};

export const getUserStaffEquipmentAssignments = async (
  userId: string
): Promise<GenericResponse<EquipmentAssignmentWithEquipmentModel[]>> => {
  try {
    const response = await get<GenericResponse>(
      `/api/staff-equipment/user-assignments/${userId}`
    );
    return response;
  } catch (error) {
    const axiosError = error as AxiosError;
    console.error("Failed to fetch user equipment assignments", axiosError);
    return { success: false, message: axiosError.message };
  }
};

export const getUsersByManager = async (managerId: string) => {
  try {
    const response = await get<User[]>(
      `/api/users/users-by-manager/${managerId}`
    );
    return response || [];
  } catch (error) {
    console.error("Failed to get users by manager", error);
    return [];
  }
};
